import { Image, Modal } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { IAttachment } from "@base/modules/appointment/appointment.interface";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg"];

const getFileName = (attachment: IAttachment) => (attachment.object_name ?? attachment.link ?? "").split("/").pop() || "Unnamed";

const isImage = (attachment: IAttachment) => IMAGE_EXTENSIONS.some((extension) => getFileName(attachment).toLowerCase().endsWith(extension));

interface IProps {
  insurance?: ILifeInsurance;
  onClose: () => void;
}

export default function InsuranceDetailModal({ insurance, onClose }: IProps) {
  //! state
  const { t } = useTranslation();

  //! function
  const formatDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "");

  const rows = [
    { label: t("user.profile.insurance.packageType"), value: insurance?.health_insurance?.provider },
    { label: t("user.profile.insurance.customerGroup"), value: insurance?.health_insurance?.name },
    { label: t("user.profile.insurance.contractNumber"), value: insurance?.card_no },
    { label: t("user.profile.insurance.effectiveDate"), value: formatDate(insurance?.start_date) },
    { label: t("user.profile.insurance.expirationDate"), value: formatDate(insurance?.end_date) },
    { label: t("user.profile.insurance.note"), value: insurance?.description },
  ].filter((row) => !!row.value);

  const images = (insurance?.images ?? []).filter(isImage);
  const files = (insurance?.images ?? []).filter((attachment) => !isImage(attachment));

  //! render
  return (
    <Modal open={!!insurance} onCancel={onClose} footer={null} centered destroyOnClose title={t("user.profile.insurance.details")}>
      <div className="flex flex-col gap-3 pt-2">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4 text-sm font-['Inter']">
            <span className="text-common-subtext shrink-0">{row.label}</span>
            <span className="text-common-maintext text-right break-words min-w-0">{row.value}</span>
          </div>
        ))}

        {insurance?.images?.length ? (
          <div className="flex flex-col gap-2">
            <div className="text-common-subtext text-sm font-['Inter']">{t("user.profile.insurance.attachments")}</div>

            {images.length ? (
              <div className="grid grid-cols-2 gap-2">
                <Image.PreviewGroup>
                  {images.map((attachment) => (
                    <CommonStyles.ImageUI
                      key={attachment.object_name ?? attachment.link}
                      className="rounded-lg object-cover aspect-[4/3]"
                      width="100%"
                      src={attachment.link}
                    />
                  ))}
                </Image.PreviewGroup>
              </div>
            ) : null}

            {files.map((attachment) => (
              <a
                key={attachment.object_name ?? attachment.link}
                href={attachment.link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 bg-common-background rounded-lg p-3 text-common-maintext text-sm hover:underline"
              >
                <CommonIcons.IconTextSnippet />
                <span className="truncate">{getFileName(attachment)}</span>
              </a>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
